import { Stack } from '@chakra-ui/react';
import React from 'react';

import { MedicalFSABenefits } from 'Containers/ViewBenefitPage/types';
import { transformBenefitItems } from 'Containers/ViewBenefitPage/utils';
import { useTextContext } from 'Contexts/textContext';
import { H5, Numbers, Paragraph } from 'DesignLibrary/atoms';
import RichText from 'Shared/RichText';
import Text from 'Shared/Text';
import { SupplementalPlan } from 'Types/entities';
import { formatDate, toDollars } from 'Utils/helpers';
import { useFederalLimit } from 'Utils/useFederalLimit';

import { InfoBox, LimitRow } from './fsaPlanContent.styled';
import { DetailsWrapper } from './PlanContent/styled';

export interface FsaPlanContentProps {
  plan: SupplementalPlan;
  isLimitedPurpose: boolean;
}

/**
 * Renders healthcare FSA content (or Limited Purpose FSA when the user's selected
 * medical plan is HSA-eligible).
 *
 * NOTE: `plan` is the healthcare FSA linked to the user's eligible *tax savings plan*
 * not the FSA linked to their *medical plan*.
 *
 * Currently, we assume any FSA linked to the selected medical plan is the same
 * plan referenced here (the expected customer configuration).
 *
 * While it is possible to configure Builder in such a way that
 * a user could be eligible for one FSA plan via their medical plan
 * and a different FSA (or no FSA!) via their tax savings plan,
 * it is not something we expect to actually see in a real implementation
 * and would consider a misconfig.
 */
export const FsaPlanContent = ({ plan, isLimitedPurpose }: FsaPlanContentProps) => {
  const { locale, retrieveContentfulData } = useTextContext();

  const benefitItems = transformBenefitItems(plan) as MedicalFSABenefits;

  const fs = plan.fsa_specific;
  const unwrap = (value: any) => (value && typeof value === 'object' && 'data' in value ? value.data : value);

  const jvpFederalLimit = useFederalLimit('medical_fsa');
  const federalLimit =
    jvpFederalLimit ??
    retrieveContentfulData<number>('benefits_section_medical_fsa.federal_contribution_limit');
  const contributionLimit = benefitItems.uses_federal_limit
    ? federalLimit
    : benefitItems.maximum_contribution;

  const rolloverAmount = unwrap(fs?.rollover_amount_limit);
  const hasRollover = !!rolloverAmount;

  const incurDate = unwrap(fs?.grace_period);
  const submitDate = unwrap(fs?.rollover_end_date);
  const hasDeadlines = !!incurDate && !!submitDate;

  const eligibleExpensesList = isLimitedPurpose
    ? 'benefits_section_medical_fsa.lpfsa_eligible_expenses_list'
    : 'benefits_section_medical_fsa.eligible_expenses_list';

  const deadlineWithRollover = isLimitedPurpose
    ? 'benefits_section_medical_fsa.lpfsa_deadlines_with_rollover'
    : 'benefits_section_medical_fsa.deadlines_with_rollover';
  const deadlineNoRollover = isLimitedPurpose
    ? 'benefits_section_medical_fsa.lpfsa_deadlines_no_rollover'
    : 'benefits_section_medical_fsa.deadlines_no_rollover';

  return (
    <DetailsWrapper>
      <Stack gap={6}>
        {/* 1. Contribution limit -------------------------------------------- */}
        <LimitRow data-testid="fsa-contribution-limit">
          <H5>
            <Text field="benefits_section_medical_fsa.contribution_limit_label" />
          </H5>
          <Numbers size="small" weight="normal">
            {toDollars(contributionLimit)}
          </Numbers>
        </LimitRow>

        {/* 2. Eligible expenses --------------------------------------------- */}
        <Stack gap={2}>
          <H5>
            <Text field="benefits_section_medical_fsa.eligible_expenses_title" />
          </H5>
          <Paragraph size="small" lineHeight={1.75}>
            <Text
              field={
                isLimitedPurpose
                  ? 'benefits_section_medical_fsa.lpfsa_eligible_expenses_intro'
                  : 'benefits_section_medical_fsa.eligible_expenses_intro'
              }
            />
          </Paragraph>
          <InfoBox>
            <RichText
              field={eligibleExpensesList}
              inlineParagraphs={false}
              useStylingWrapper
              richTextStyling={{ p: { size: 'small', lineHeight: 1.75 } }}
              maxHeaderSize={4}
            />
          </InfoBox>
          {!isLimitedPurpose && (
            <Paragraph size="small" lineHeight={1.75}>
              <Text
                field="benefits_section_medical_fsa.eligible_expenses_irs_note"
                vars={{
                  irsLink: (
                    <a href="https://www.irs.gov/pub/irs-pdf/p502.pdf" target="_blank" rel="noreferrer">
                      IRS publication 502
                    </a>
                  ),
                }}
              />
            </Paragraph>
          )}
        </Stack>

        {/* 3. Important deadlines ------------------------------------------- */}
        {hasDeadlines && (
          <Stack gap={2}>
            <H5>
              <Text field="benefits_section_medical_fsa.important_deadlines_title" />
            </H5>
            <Paragraph size="small" lineHeight={1.75}>
              {hasRollover ? (
                <Text
                  field={deadlineWithRollover}
                  vars={{
                    rolloverAmt: toDollars(rolloverAmount),
                    incurDate: formatDate(incurDate as string, locale),
                    submitDate: formatDate(submitDate as string, locale),
                  }}
                />
              ) : (
                <Text
                  field={deadlineNoRollover}
                  vars={{
                    incurDate: formatDate(incurDate as string, locale),
                    submitDate: formatDate(submitDate as string, locale),
                  }}
                />
              )}
            </Paragraph>
          </Stack>
        )}
      </Stack>
    </DetailsWrapper>
  );
};
